import React, { useEffect, useRef } from "react";
import { Attribute, FieldType, Option, getFileExtensionTypeString } from "../../../../model/service-request/attribute";
import { DynamicFieldItem } from "../../../../model/dynamic-forms";
import { Money } from "../../../../model/common/money";
import {
	TextInputField,
	NumberStepperField,
	ConditionalDropdownField,
	ConditionalParentOption,
	DateInputField,
	FileUploadField,
} from "../form-fields";
import ToggleField from "../toggle/ToggleField";
import SelectDropdownField, { type DropdownOption } from "../select-dropdown/SelectDropdownField";
import FieldLabel from "./FieldLabel";
import { buildDynamicFieldItem } from "./engine/buildDynamicFieldItem";
import { isFieldFilled } from "./engine/dynamicFormValidator";
import styles from "./dynamic-field.module.scss";

export interface DynamicFieldProps {
	attribute: Attribute;
	preselectedValue?: DynamicFieldItem;
	isSubmitted: boolean;
	onChange: (item: DynamicFieldItem) => void;
	onRemove: (fieldId: string) => void;
}

const formatPrice = (price?: Money): string => {
	if (!price) return "";
	return ` ${new Money(price.currency, price.amount).toLocaleString()}`;
};

const optionLabel = (option: Option): string => `${option.value}${formatPrice(option.price)}`;

const toDropdownOptions = (options: Option[] = []): DropdownOption[] =>
	options.map(o => ({ value: o.id ?? o.value, label: optionLabel(o) }));

const findOptionPrice = (options: Option[] = [], value: string): Money | undefined =>
	options.find(o => (o.id ?? o.value) === value)?.price;

const BooleanField: React.FC<DynamicFieldProps> = ({ attribute, preselectedValue, onChange }) => {
	const initial = (preselectedValue?.fieldValue as boolean | undefined) ?? attribute.defaultState ?? false;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	useEffect(() => {
		if (preselectedValue === undefined) {
			onChangeRef.current(buildDynamicFieldItem({ attribute, fieldValue: initial }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checked = (preselectedValue?.fieldValue as boolean | undefined) ?? initial;
	return (
		<ToggleField checked={checked} onChange={value => onChange(buildDynamicFieldItem({ attribute, fieldValue: value }))} />
	);
};

/** Renders a single dynamic field by its type, mapping the value into a DynamicFieldItem via the engine. */
const DynamicField: React.FC<DynamicFieldProps> = props => {
	const { attribute, preselectedValue, isSubmitted, onChange, onRemove } = props;
	const hasError = isSubmitted && attribute.isRequired && !isFieldFilled(preselectedValue);

	const labelText = attribute.label;

	const control = (() => {
		switch (attribute.fieldType) {
			case FieldType.Text:
				return (
					<TextInputField
						value={preselectedValue?.fieldValue ?? ""}
						placeholder={attribute.placeholderText}
						maxLength={attribute.isLimited ? attribute.maxLengthLimit : undefined}
						hasError={hasError}
						onChange={value =>
							value ? onChange(buildDynamicFieldItem({ attribute, fieldValue: value })) : onRemove(attribute.id)
						}
					/>
				);

			case FieldType.TextDescription:
				return (
					<TextInputField
						multiline
						value={preselectedValue?.fieldValue ?? ""}
						placeholder={attribute.placeholderText}
						maxLength={attribute.isLimited ? attribute.maxLengthLimit : undefined}
						hasError={hasError}
						onChange={value =>
							value ? onChange(buildDynamicFieldItem({ attribute, fieldValue: value })) : onRemove(attribute.id)
						}
					/>
				);

			case FieldType.Number:
				return (
					<NumberStepperField
						value={preselectedValue?.fieldValue != null ? String(preselectedValue.fieldValue) : ""}
						placeholder={attribute.placeholderText}
						min={attribute.minNumberValue}
						max={attribute.maxNumberValue}
						hasError={hasError}
						onChange={value =>
							value ? onChange(buildDynamicFieldItem({ attribute, fieldValue: value })) : onRemove(attribute.id)
						}
					/>
				);

			case FieldType.Bool:
				return <BooleanField {...props} />;

			case FieldType.Dropdown: {
				const options = attribute.childrenOptionDefinitions ?? [];
				const dropdownOptions = toDropdownOptions(options);
				const multiple = attribute.isSingleType === false;

				if (multiple) {
					const selectedValues: string[] = Array.isArray(preselectedValue?.selectedChildIds)
						? preselectedValue!.selectedChildIds!
						: Array.isArray(preselectedValue?.fieldValue)
						? preselectedValue!.fieldValue
						: [];
					return (
						<SelectDropdownField
							multiple
							options={dropdownOptions}
							placeholder={attribute.placeholderText}
							selectedValues={selectedValues}
							hasError={hasError}
							onSelectMulti={opts => {
								if (opts.length === 0) {
									onRemove(attribute.id);
									return;
								}
								onChange(
									buildDynamicFieldItem({
										attribute,
										isMulti: true,
										fieldValue: opts.map(o => o.value),
										selectedChildIds: opts.map(o => o.value),
										dropdownText: opts.map(o => o.label).join(", "),
									})
								);
							}}
						/>
					);
				}

				const selectedValue = preselectedValue?.selectedChildId ?? preselectedValue?.fieldValue;
				return (
					<SelectDropdownField
						options={dropdownOptions}
						placeholder={attribute.placeholderText}
						selectedValue={selectedValue}
						hasError={hasError}
						onSelectSingle={opt =>
							onChange(
								buildDynamicFieldItem({
									attribute,
									fieldValue: opt.value,
									selectedChildId: opt.value,
									dropdownText: opt.label,
									price: findOptionPrice(options, opt.value),
								})
							)
						}
					/>
				);
			}

			case FieldType.ConditionalDropdown: {
				const options = attribute.childrenOptionDefinitions ?? [];
				const parentOptions: ConditionalParentOption[] = options.map(parent => ({
					value: parent.id ?? parent.value,
					label: optionLabel(parent),
					children: toDropdownOptions(parent.childOptions),
				}));
				const selectedChildValue = preselectedValue?.selectedChildId ?? preselectedValue?.fieldValue;
				return (
					<ConditionalDropdownField
						parentOptions={parentOptions}
						childLabel={attribute.label}
						parentPlaceholder={attribute.placeholderText}
						selectedChildValue={selectedChildValue}
						hasError={hasError}
						onChildSelected={child => {
							if (!child) {
								onRemove(attribute.id);
								return;
							}
							onChange(
								buildDynamicFieldItem({
									attribute,
									fieldValue: child.value,
									selectedChildId: child.value,
									dropdownText: child.label,
								})
							);
						}}
					/>
				);
			}

			case FieldType.DateTime:
				return (
					<DateInputField
						value={preselectedValue?.fieldValue ?? undefined}
						placeholder={attribute.placeholderText}
						hasError={hasError}
						onChange={iso =>
							iso ? onChange(buildDynamicFieldItem({ attribute, fieldValue: iso })) : onRemove(attribute.id)
						}
					/>
				);

			case FieldType.File:
			case FieldType.Image: {
				const allowedExtensions = (attribute.fileExtensionTypes ?? [])
					.map(ext => getFileExtensionTypeString(ext))
					.filter((e): e is NonNullable<typeof e> => e != null);
				const file = preselectedValue?.fieldValue instanceof File ? preselectedValue.fieldValue : null;
				return (
					<FileUploadField
						file={file}
						fileName={preselectedValue?.fileFileName}
						allowedExtensions={allowedExtensions}
						maxFileSizeBytes={attribute.fileSize && attribute.fileSize > 0 ? attribute.fileSize : undefined}
						hasError={hasError}
						onFileSelected={selected =>
							onChange(buildDynamicFieldItem({ attribute, fieldValue: selected, fileFileName: selected.name }))
						}
						onCleared={() => onRemove(attribute.id)}
					/>
				);
			}

			default:
				return (
					<div className={styles.notImplemented}>
						This field type is not supported yet
					</div>
				);
		}
	})();

	return (
		<FieldLabel label={labelText} required={attribute.isRequired}>
			{control}
		</FieldLabel>
	);
};

export default DynamicField;
